/**
 * Reabrir processamento fechado (pedido do dono, 21/08/2026 — "preciso ter a
 * opção de reverter se preciso"). Botão + modal com motivo obrigatório; os
 * avisos da API (ex.: folha do mês fechada não muda; rescisão desfaz o
 * desligamento cadastral) voltam para a página exibir. Usado nas páginas de
 * recibo de férias, 13º e rescisão.
 */
import { useState } from "react";
import { Button, Group, Modal, Text, Textarea } from "@mantine/core";
import { IconLockOpen } from "@tabler/icons-react";
import { api } from "../api";
import { notificarOk, notificarAviso, notificarErroApi } from "./notificacoes";

export function ReabrirProcessamento({
  processamentoId,
  tipoRotulo,
  onReaberto,
}: {
  processamentoId: string;
  /** Rótulo do documento para os textos ("recibo de férias" | "processamento de 13º" | "rescisão"). */
  tipoRotulo: string;
  /** Chamado após reabrir — a página recarrega o detalhe e mostra os avisos. */
  onReaberto: (avisos: string[]) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function reabrir() {
    setOcupado(true);
    setErro(null);
    try {
      const r = await api.post<{ avisos?: string[] }>(`/processamentos/${processamentoId}/reabrir`, {
        motivo: motivo.trim(),
      });
      notificarOk("Reaberto — edição e recálculo liberados.");
      for (const aviso of r.avisos ?? []) notificarAviso(aviso);
      setAberto(false);
      setMotivo("");
      onReaberto(r.avisos ?? []);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
      notificarErroApi(e);
    } finally {
      setOcupado(false);
    }
  }

  return (
    <>
      <Button
        variant="light"
        color="orange"
        leftSection={<IconLockOpen size={16} />}
        onClick={() => setAberto(true)}
        title="Reverte o fechamento: o documento volta a 'calculado' (editável/recalculável). Rescisão: desfaz o desligamento cadastral. Fica registrado na auditoria com o motivo."
      >
        Reabrir
      </Button>
      <Modal opened={aberto} onClose={() => setAberto(false)} title={`Reabrir o ${tipoRotulo} fechado?`} centered>
        <Text size="sm" mb="sm">
          O documento volta para <b>calculado</b>: a configuração pode ser editada e o cálculo refeito, e o fechamento
          poderá ser refeito depois. Se a folha do mês de pagamento já estiver fechada, ela <b>não</b> muda — para
          refletir lá, retifique a folha. A reabertura fica na auditoria com o motivo.
        </Text>
        <Textarea
          label="Motivo (obrigatório)"
          placeholder="Ex.: fechei por engano; o gozo mudou de período…"
          value={motivo}
          onChange={(e) => setMotivo(e.currentTarget.value)}
          minRows={2}
          required
          error={erro ?? undefined}
        />
        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={() => setAberto(false)} disabled={ocupado}>
            Voltar
          </Button>
          <Button color="orange" loading={ocupado} disabled={!motivo.trim()} onClick={() => void reabrir()}>
            Reabrir
          </Button>
        </Group>
      </Modal>
    </>
  );
}
